import type { TokenResponse } from "@/lib/authBackend/trimble/tid/client";

export const PROCORE_TID_POPUP_NAME = "dc-tid-popup";
export const PROCORE_TID_SILENT_FRAME_NAME = "dc-tid-silent-frame";
export const PROCORE_TID_MESSAGE_TYPE = "dc-tid-complete";

type ProcoreTidSuccessMessage = {
  type: typeof PROCORE_TID_MESSAGE_TYPE;
  requestId: string;
  success: true;
  session: TokenResponse;
};

type ProcoreTidFailureMessage = {
  type: typeof PROCORE_TID_MESSAGE_TYPE;
  requestId: string;
  success: false;
  error: string;
};

export type ProcoreTidMessage = ProcoreTidSuccessMessage | ProcoreTidFailureMessage;

export function isFramedWindow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function isProcoreReturnTo(returnTo: string): boolean {
  return /^\/(?:[a-z]{2}\/)?procore(?:[/?#]|$)/.test(returnTo);
}

export function shouldUseProcoreTidPopup(returnTo: string | undefined): boolean {
  return isFramedWindow() && !!returnTo && isProcoreReturnTo(returnTo);
}

function splitLocalePrefix(pathname: string): { localePrefix: string; path: string } {
  const localeMatch = pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  const localePrefix = localeMatch?.[1] ? `/${localeMatch[1]}` : "";
  const path = localePrefix ? pathname.slice(localePrefix.length) || "/" : pathname;
  return { localePrefix, path };
}

export function buildProcoreTidLoginUrl(pathname: string, search: string): string {
  const { localePrefix, path } = splitLocalePrefix(pathname);
  return `${localePrefix}/login?returnTo=${encodeURIComponent(`${path}${search}`)}`;
}

// Reuses the /login/tid-popup page — it already calls loginWithRedirect on mount with
// no click required, which is exactly what a hidden iframe needs to attempt a silent check.
export function buildTidSilentFrameUrl(pathname: string, search: string, requestId: string): string {
  const { localePrefix, path } = splitLocalePrefix(pathname);
  const params = new URLSearchParams({ requestId, returnTo: `${path}${search}` });
  return `${localePrefix}/login/tid-popup?${params.toString()}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNonemptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isTokenResponse(value: unknown): value is TokenResponse {
  if (!isRecord(value)) return false;
  return (
    isNonemptyString(value.access_token) &&
    isNonemptyString(value.id_token) &&
    isNonemptyString(value.refresh_token) &&
    isNonemptyString(value.code_verifier) &&
    typeof value.expires_at === "number" &&
    Number.isFinite(value.expires_at)
  );
}

function isProcoreTidMessage(value: unknown): value is ProcoreTidMessage {
  if (!isRecord(value) || value.type !== PROCORE_TID_MESSAGE_TYPE || !isNonemptyString(value.requestId)) {
    return false;
  }
  if (value.success === true) return isTokenResponse(value.session);
  return value.success === false && isNonemptyString(value.error);
}

export function isTidPopupWindow(): boolean {
  if (typeof window === "undefined") return false;
  return window.name === PROCORE_TID_POPUP_NAME && window.opener != null && window.opener !== window;
}

export function notifyOpenerTidComplete(message: ProcoreTidMessage): void {
  if (typeof window === "undefined" || !window.opener) return;
  window.opener.postMessage(message, window.location.origin);
  window.close();
}

export function isSilentTidFrame(): boolean {
  if (typeof window === "undefined") return false;
  return window.name === PROCORE_TID_SILENT_FRAME_NAME && window.parent !== window;
}

// No window.close() — an iframe can't close itself; the parent removes it from the DOM
// once this message arrives (see useSilentTidFrameCheck).
export function notifyParentTidSilentComplete(message: ProcoreTidMessage): void {
  if (typeof window === "undefined" || window.parent === window) return;
  window.parent.postMessage(message, window.location.origin);
}

export function isTidPopupMessageForAttempt(
  event: MessageEvent<unknown>,
  popup: Window,
  requestId: string,
  expectedOrigin: string,
): event is MessageEvent<ProcoreTidMessage> {
  return (
    event.origin === expectedOrigin &&
    event.source === popup &&
    isProcoreTidMessage(event.data) &&
    event.data.requestId === requestId
  );
}
